/**
 * Contrato de quem entra no Concierge e do que cada pessoa pode abrir.
 *
 * Papel: aqui ficam as chaves dos módulos, os dois níveis de permissão, o
 * formato de um usuário e o da resposta que diz ao front o que desenhar.
 *
 * Como usar: `CHAVES_MODULO` é a lista de onde saem o enum do Postgres, o
 * enum do Zod e a grade da tela de permissões — um módulo novo começa pela
 * chave aqui, e não em cada um dos três lugares. A chave nova também precisa de
 * uma entrada em `MODULOS`, em `apps/web/src/app/modulos.ts`, e de uma migration
 * que acrescente o valor ao enum `modulo`. Sem a entrada em `MODULOS`, o módulo
 * aparece na grade e pode ser concedido, mas a trilha do `Layout` some da tela
 * dele, porque `infoDaTrilha` não acha rota que case com o endereço; o teste
 * `modulos.test.ts` é o que acusa essa falta. O cartão da tela inicial é uma
 * terceira entrada, em `CARTOES_DA_TELA_INICIAL`, que só existe para os módulos
 * que têm cartão próprio.
 *
 * Permissão ausente é permissão negada: quem não tem linha para um módulo não
 * o enxerga. Não existe nível `nenhum` gravado, porque um nível assim teria
 * duas formas de dizer a mesma coisa — a linha com `nenhum` e a linha que não
 * existe — e toda consulta passaria a testar as duas.
 */
import { z } from 'zod';

import { idSchema } from './comum';

/**
 * As chaves dos módulos que podem ser liberados a um usuário.
 *
 * A ordem é a das linhas da grade de permissões, que lê `MODULOS_CONCEDIVEIS`
 * filtrado daqui — mexer nela reposiciona todos os módulos da grade de uma vez
 * para quem já sabe de cor onde cada um fica. A tela inicial não segue esta
 * ordem: a dos cartões é a de `CARTOES_DA_TELA_INICIAL`.
 */
export const CHAVES_MODULO = [
  'financas',
  'rh',
  'veiculos',
  'residencias',
  'calendario-boa-vista',
  'manutencoes-boa-vista',
  'orcamentos',
  'compras',
  'mailing',
  'saude',
  'documentos',
  'cadastros-base',
  'usuarios',
] as const;

/** Chave de um módulo, como está em `CHAVES_MODULO`. */
export const moduloSchema = z.enum(CHAVES_MODULO, { message: 'Módulo desconhecido.' });

export type ChaveModulo = z.infer<typeof moduloSchema>;

/**
 * Como cada módulo é chamado na tela.
 *
 * Fica no pacote compartilhado, e não no front, porque dois lugares distantes
 * precisam do mesmo rótulo: a grade de permissões, dentro do módulo de
 * usuários, e a tela inicial, em `app/`. Um módulo não alcança `app/`, então
 * manter a lista lá obrigaria a grade a ter a sua própria cópia — e as duas
 * divergiriam na primeira vez que um módulo fosse renomeado.
 *
 * O tipo `Record<ChaveModulo, string>` é o que obriga a completar esta lista ao
 * acrescentar uma chave em `CHAVES_MODULO`: sem o rótulo, o projeto não compila.
 */
export const ROTULOS_MODULO: Record<ChaveModulo, string> = {
  financas: 'Finanças',
  rh: 'RH doméstico',
  veiculos: 'Veículos',
  residencias: 'Residências',
  'calendario-boa-vista': 'Calendário Boa Vista',
  'manutencoes-boa-vista': 'Manutenções Boa Vista',
  orcamentos: 'Orçamentos',
  compras: 'Compras & estoque',
  mailing: 'Mailing',
  saude: 'Saúde',
  documentos: 'Documentos & segurança',
  'cadastros-base': 'Cadastros-base',
  usuarios: 'Usuários e permissões',
};

/** Chave de um módulo que pode receber nível na grade de permissões. */
export type ChaveModuloConcedivel = Exclude<ChaveModulo, 'usuarios'>;

/**
 * Os módulos que a grade de permissões oferece, na ordem de `CHAVES_MODULO`.
 *
 * `usuarios` fica de fora porque a tela de usuários é aberta pelo papel
 * `concierge:admin`, e não por nível gravado em `permissoes_modulo`: uma linha
 * concedendo `usuarios` não abriria tela nenhuma, e a grade mostraria a quem
 * administra um controle que não controla nada. A chave continua em
 * `CHAVES_MODULO` porque o enum `modulo` do Postgres nasce de lá, e tirá-la
 * geraria uma migration de remoção de valor de enum, que o Postgres só executa
 * recriando o tipo e reescrevendo a tabela que o usa.
 */
export const MODULOS_CONCEDIVEIS: readonly ChaveModuloConcedivel[] = CHAVES_MODULO.filter(
  (chave): chave is ChaveModuloConcedivel => chave !== 'usuarios',
);

/**
 * Os níveis, do menor para o maior.
 *
 * A ordem importa: `nivelAtende` compara pelo índice nesta lista, de modo que
 * quem tem `editar` satisfaz uma exigência de `ver` sem precisar de duas
 * linhas. Um nível novo precisa entrar na posição certa, não no fim.
 */
export const NIVEIS_PERMISSAO = ['ver', 'editar'] as const;

/** Nível de acesso de um usuário a um módulo. */
export const nivelPermissaoSchema = z.enum(NIVEIS_PERMISSAO, {
  message: 'Nível de permissão inválido.',
});

export type NivelPermissao = z.infer<typeof nivelPermissaoSchema>;

/**
 * Diz se o nível concedido cobre o que se exige.
 *
 * É a regra única de comparação entre níveis: `exigirPermissao`, na API, decide
 * com ela se a requisição passa, e `acessoAlcanca` decide com ela, na tela, se
 * o cartão e o botão aparecem. Duas cópias da comparação divergiriam no dia em
 * que um nível fosse acrescentado, e a tela passaria a oferecer um botão que a
 * API recusa.
 *
 * @param concedido - Nível gravado para o usuário no módulo.
 * @param exigido - Nível que a rota ou a tela pede, `'ver'` para leitura e
 *   `'editar'` para escrita.
 * @returns Verdadeiro quando o concedido está na mesma posição ou depois do
 *   exigido em `NIVEIS_PERMISSAO`.
 */
export function nivelAtende(concedido: NivelPermissao, exigido: NivelPermissao): boolean {
  return NIVEIS_PERMISSAO.indexOf(concedido) >= NIVEIS_PERMISSAO.indexOf(exigido);
}

/** O que um usuário pode fazer em um módulo. */
export const permissaoSchema = z.object({
  modulo: moduloSchema,
  nivel: nivelPermissaoSchema,
});

export type Permissao = z.infer<typeof permissaoSchema>;

/**
 * Um usuário com conta, como a listagem da tela de usuários o mostra.
 *
 * Não há campo dizendo se a pessoa é administradora. O papel `concierge:admin`
 * vive no token de cada um e só é lido quando aquela pessoa faz uma requisição,
 * de modo que a listagem, feita pelo token de outra pessoa, não tem de onde
 * tirá-lo; um campo assim sairia sempre falso e a tela ofereceria editar a
 * grade de quem já tem acesso a tudo como se não tivesse nada. Promover alguém
 * acontece no Auth0.
 *
 * `ultimoAcessoEm` é um instante ISO 8601 com fuso, ou `null` para quem ganhou
 * linha e ainda não voltou a entrar.
 */
export const usuarioSchema = z.object({
  id: idSchema,
  auth0Sub: z.string().min(1),
  email: z.string().nullable(),
  nome: z.string().nullable(),
  ultimoAcessoEm: z.iso.datetime({ offset: true }).nullable(),
  permissoes: z.array(permissaoSchema),
});

export type Usuario = z.infer<typeof usuarioSchema>;

/**
 * O conjunto de permissões que substitui o atual de um usuário.
 *
 * A substituição é do conjunto inteiro, e não de uma permissão por vez, porque
 * a tela é uma grade que se salva de uma vez: enviar só o que mudou exigiria
 * que o front soubesse o estado anterior, e duas pessoas editando a mesma grade
 * acabariam somando permissões que nenhuma das duas concedeu.
 *
 * Um módulo repetido é recusado em vez de sobrescrito — repetição é sinal de
 * defeito em quem montou o pedido, e aceitar a última ocorrência esconderia o
 * problema atrás de um resultado plausível. Pelo mesmo motivo é recusado o
 * módulo que não está em `MODULOS_CONCEDIVEIS`: gravá-lo não daria acesso a
 * nada, e quem chamou sairia achando que deu.
 */
export const definirPermissoesSchema = z.object({
  permissoes: z
    .array(permissaoSchema)
    .max(MODULOS_CONCEDIVEIS.length)
    .refine((lista) => new Set(lista.map((p) => p.modulo)).size === lista.length, {
      message: 'Há módulo repetido na lista de permissões.',
    })
    .refine((lista) => lista.every((p) => p.modulo !== 'usuarios'), {
      message: 'O acesso à tela de usuários vem do papel de administrador, e não da grade.',
    }),
});

export type DefinirPermissoes = z.infer<typeof definirPermissoesSchema>;

/**
 * O que `PUT /api/usuarios/{id}/permissoes` devolve: o conjunto como ficou.
 *
 * A resposta repete a lista em vez de responder sem corpo para a tela poder
 * conferir que o gravado é o que foi pedido, e para o `clienteApi` ter um
 * schema contra o qual validar o sucesso.
 */
export const permissoesSalvasSchema = z.object({
  id: idSchema,
  permissoes: z.array(permissaoSchema),
});

export type PermissoesSalvas = z.infer<typeof permissoesSalvasSchema>;

/**
 * O que a rota de sessão devolve a quem acabou de entrar.
 *
 * É a partir daqui que a tela inicial decide quais cartões desenhar e que cada
 * página decide se mostra o botão de salvar. Um administrador vem com
 * `permissoes` vazio e `eAdministrador` verdadeiro — o front precisa tratar o
 * papel antes da lista, senão quem administra não enxerga módulo nenhum; é o que
 * `acessoAlcanca` faz.
 */
export const acessoSchema = z.object({
  id: idSchema,
  auth0Sub: z.string().min(1),
  nome: z.string().nullable(),
  eAdministrador: z.boolean(),
  permissoes: z.array(permissaoSchema),
});

export type Acesso = z.infer<typeof acessoSchema>;

/**
 * Diz se um acesso alcança o nível pedido num módulo.
 *
 * Quem chama é a tela, para decidir se desenha o cartão de um módulo (`'ver'`)
 * ou o botão que grava (`'editar'`). O papel de administrador é testado antes da
 * lista, porque `registrarAcesso` devolve `permissoes` vazio para quem o tem, e
 * ler só a lista esconderia todos os módulos justamente de quem administra.
 *
 * O resultado decide o que aparece, e não o que é permitido: a API refaz a
 * verificação em `exigirPermissao`, e um botão exibido por engano leva a um 403,
 * nunca a uma gravação.
 *
 * @param acesso - O que `GET /api/sessao` devolveu para a pessoa logada.
 * @param modulo - Chave do módulo, como está em `CHAVES_MODULO`.
 * @param nivel - `'ver'` para mostrar, `'editar'` para permitir alterar.
 * @returns Verdadeiro para administrador, ou quando há linha para o módulo
 *   com nível suficiente.
 */
export function acessoAlcanca(
  acesso: Pick<Acesso, 'eAdministrador' | 'permissoes'>,
  modulo: ChaveModulo,
  nivel: NivelPermissao,
): boolean {
  if (acesso.eAdministrador) return true;
  const concedida = acesso.permissoes.find((permissao) => permissao.modulo === modulo);
  return concedida !== undefined && nivelAtende(concedida.nivel, nivel);
}
